import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { AppHeader } from '../layout/Header/Header';
import { Spinner } from '../ui/Spinner/Spinner';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useSyncStore } from '../stores/syncStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { InitialSync } from './InitialSync/InitialSync';
import { Onboarding } from './Onboarding/Onboarding';
import { AcombaCopyBanner } from '../layout/AcombaCopyBanner/AcombaCopyBanner';
import { BridgeOutdatedBanner } from '../layout/BridgeOutdatedBanner/BridgeOutdatedBanner';
import { useResumeSync } from '../hooks/useResumeSync';
import { useRelanceFillTracker } from '../hooks/useRelanceFillTracker';
import { useDailySync } from '../hooks/useDailySync';
import { useOnboardingGate } from '../hooks/useOnboardingGate';
import { useRecordTranspherePaymentsApplied } from '../hooks/useRecordTranspherePaymentsApplied';
import { transphereService } from '../services/transphereService';
import { useIsAcomba } from '../hooks/useIsAcomba';
import { useAcombaBridgeGate } from '../hooks/useAcombaBridgeGate';
import { AcombaBridgeConnectingScreen } from './Acomba/AcombaBridgeConnectingScreen';
import { AcombaBridgeBlockedScreen } from './Acomba/AcombaBridgeBlockedScreen';
import { useIsScrolled } from '../hooks/useIsScrolled';
import { TranslationKey } from '../i18n/translationKeys';

export const RootLayout: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { ready, error } = useAuthGuard();
    const initialSyncRequired = useSyncStore(s => s.initialSyncRequired);
    const onboardingRequired = useOnboardingStore(s => s.onboardingRequired);
    const onboardingResolved = useOnboardingStore(s => s.onboardingResolved);
    const isAcomba = useIsAcomba();
    const bridgeGate = useAcombaBridgeGate(isAcomba);
    const isScrolled = useIsScrolled();
    // /acomba (BridgeWizard) is the dedicated manual diagnostic page for exactly this situation — it
    // must stay reachable while the bridge is down, or a broken bridge would trap the user behind the
    // gate below with no way to reach the page that explains why and offers a fix.
    const isAcombaDiagnosticsPage = location.pathname === '/acomba';

    useResumeSync(ready, initialSyncRequired);
    useRelanceFillTracker();
    useDailySync(ready, initialSyncRequired);
    useOnboardingGate(ready);

    // Placed on the shell, not on Home, so the "Paiements appliqués Transphere" history entry is recorded whatever
    // route is showing when the operator saves in AcombaGO's Encaissement window — reached via the
    // banner's button or opened manually. Fire-and-forget: a failed record must never disrupt the session.
    useRecordTranspherePaymentsApplied((customersUpdated, invoicesApplied) => {
        void transphereService.recordPaymentsApplied(customersUpdated, invoicesApplied).catch(() => undefined);
    });

    if (error) return (
        <div data-testid="portal-auth-error" className="flex min-h-screen items-center justify-center px-xl">
            <p className="text-center text-primary-700">{t(TranslationKey.Auth.PortalError)}</p>
        </div>
    );
    // onboardingResolved is checked here too, not only ready: unlike initialSyncRequired (set
    // synchronously alongside ready by useAuthGuard), the onboarding decision is fetched by a separate
    // effect that only starts once ready is true. Falling through on ready alone let Home (or
    // InitialSync) render for a frame with the store's default onboardingRequired=false, before that
    // fetch came back — visible on Acomba desktop launches as a flash of the relance list ahead of the
    // walkthrough.
    if (!ready || !onboardingResolved) return (
        <div data-testid="auth-loading" className="flex min-h-screen items-center justify-center bg-surface">
            <div className="flex flex-col items-center gap-m">
                <Spinner className="w-6 h-6" sparks />
                <p className="text-s text-primary-700">{t('auth.loading')}</p>
            </div>
        </div>
    );

    // The company's acceptance of the terms of use (Loi 25) gates everything else, including the
    // initial sync — no sync may run before an administrator has answered. Takes priority over
    // initialSyncRequired below, which the walkthrough's own last screen resolves once it is given.
    if (onboardingRequired) return <Onboarding />;

    // App-wide bridge connection gate — never let the shell (or InitialSync below) render while an
    // Acomba desktop session's bridge isn't confirmed 'connected': a half-connected bridge produced a
    // greeting with no name and let a stale-looking session through to downstream tenant resolution.
    // Not gated by isAcomba here — useAcombaBridgeGate itself only enters 'checking'/'blocked' for a
    // desktop session, so it's always 'ready' (a no-op) for AcceoConnect/AcombaGo. Exempts /acomba
    // (see above).
    if (!isAcombaDiagnosticsPage) {
        if (bridgeGate.status === 'checking') return <AcombaBridgeConnectingScreen />;
        if (bridgeGate.status === 'blocked') return <AcombaBridgeBlockedScreen />;
    }

    // Show full-screen initial sync overlay before letting the user into the app.
    // Only reachable via the auth flow (store flag) — there is no /initial-sync route.
    if (initialSyncRequired) return <InitialSync />;

    return (
        <div className="min-h-screen bg-surface text-primary-1250">
            <div
                className={`sticky top-0 z-50 transition-colors duration-200 ${isScrolled ? 'bg-surface/70 backdrop-blur-md' : 'bg-surface'
                    }`}
            >
                <div className="mx-auto w-full max-w-content px-page pt-s">
                    <AppHeader />
                </div>
            </div>
            <div className="mx-auto w-full max-w-content px-page pt-m pb-3xl">
                <AcombaCopyBanner />
                <BridgeOutdatedBanner />
                <main>
                    <Outlet />
                </main>
            </div>
            {/* A new page opens at the top; Back returns to where the previous page was scrolled. */}
            <ScrollRestoration />
        </div>
    );
};
